/**
 * Economy — редактор `game_configs`.
 *
 * Здесь живут числа игры (правило №3): награды, цены, пороги. Правятся без
 * деплоя, и это единственная причина, по которой раздел стоит первым.
 *
 * Три вещи, которые раздел обязан показывать, и все три — не украшение:
 *
 *  • **Схема.** Каждый ключ проверяется своей zod-схемой из `@lust/shared` —
 *    той же, которой пользуются сид и чтение конфига на сервере. Значение,
 *    не прошедшее её, не сохраняется: сервер отвечает 422 и в базу ничего
 *    не пишет. Клиентская проверка здесь только ради скорости ответа.
 *
 *  • **Версию.** По ней видно, правился ли конфиг с момента заливки.
 *
 *  • **Владельца строки** (`source`). Пока он `seed`, значение перепишет
 *    ближайшая выкатка: сид гоняется в `preDeployCommand`. Первая правка
 *    отсюда переводит строку в `admin`, и файл в репозитории её больше
 *    не трогает. Знать это надо ДО правки — потому бейдж и стоит рядом
 *    с кнопкой, а не в подписи мелким шрифтом.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { GAME_CONFIG_KEYS, parseGameConfig, type GameConfigKey } from '@lust/shared'
import { request } from '../api/client.js'
import type { ConfigRow, ConfigsResponse } from '../api/types.js'
import { AuditTrail } from '../components/AuditTrail.js'
import { EditForm } from '../components/EditForm.js'
import { PageHead } from '../components/Layout.js'
import { Card, Notice, SourceBadge, formatDate } from '../components/ui.js'
import { diffLeaves } from '../lib/diff.js'

/** Подписи полей. Только оформление — правду о структуре знает схема. */
const LABELS: Record<string, string> = {
  'starting_wallet.gems': '💎 gems',
  'starting_wallet.gold': '🪙 gold',
  'starting_wallet.energy': '⚡ energy',
  'energy.cap': 'tank ceiling',
  'energy.regenSeconds': 'seconds per 1 ⚡',
  'energy.battleCost': 'battle cost, ⚡',
  'energy.defeatRefund': 'refund on defeat, ⚡',
  'battle_rewards.win.gold': 'win · 🪙',
  'battle_rewards.win.xp': 'win · xp',
  'battle_rewards.win.affinity': 'win · 💜',
  'battle_rewards.loss.gold': 'loss · 🪙',
  'battle_rewards.loss.xp': 'loss · xp',
  'battle_rewards.loss.affinityPenalty': 'loss · 💜 taken away',
  'battle_rewards.assistantAffinityMultiplier': 'assistant 💜 multiplier',
  'battle_rewards.abandonAfterHours': 'Saved battle lifetime (hours)',
  'battle_rewards.accountXpPerLevel': 'XP per account level',
  'affinity.dailyAttention': 'daily “attention”, 💜',
  'affinity.sceneWatch': 'first full scene watch, 💜',
  'chat.comebackHours': 'silence before “come back”, h',
  'chat.affinityCloseWindow': 'window for “we got closer”, 💜',
}

const HINTS: Record<string, string> = {
  'energy.cap':
    'Read together with the battle cost and the refill rate: cap ÷ cost is how many battles fit in a full tank, cap × seconds is how long a full tank takes to come back. The slice is calibrated at two battles and 24 hours.',
  'energy.regenSeconds': 'Seconds for a single ⚡. 43200 = 12 h, so a two-battle tank returns in a day.',
  'energy.defeatRefund':
    'Never above the battle cost — otherwise a battle becomes a source of energy. On a two-battle tank keep it at 0: any refund means a defeat does not count as one of the two.',
  'battle_rewards.win.affinity':
    'Only a win pays 💜 — a defeat has no such field on purpose, it has a penalty instead. A lost battle is no more expensive than a won one, so paying it affinity made throwing a battle the fastest way to a scene.',
  'battle_rewards.loss.affinityPenalty':
    'How much 💜 a defeat takes away. Positive number, subtracted by the server; the scale never goes below zero. Not multiplied for the assistant — the multiplier is the reward for picking her, and doubling the punishment would make picking anyone a mistake. Above win 💜 × multiplier the scale goes backwards at a 50% win rate.',
  'battle_rewards.assistantAffinityMultiplier': 'This multiplier is the whole reason an assistant gets picked.',
  'battle_rewards.win.gold':
    'Read it per day, not per battle: a full tank is two battles, so this number times two is the daily income — and gold buys 💜 through the bouquet.',
  'chat.comebackHours':
    'Checked when the player comes back, not on a schedule: push and cron arrive after the slice.',
  'chat.affinityCloseWindow':
    'How many 💜 short of the scene gate the assistant starts hinting at it. Wider than the gate and “almost there” becomes a lie.',
}

const DESCRIPTIONS: Record<string, string> = {
  starting_wallet: 'Starting wallet of a new player. Credited through the ledger.',
  energy:
    'The energy tank: ceiling, refill rate, battle price. Calibrated as two battles per tank and a full day to refill it — the three numbers only mean anything together.',
  battle_rewards:
    'Battle rewards. A level may override them with its own fields. A defeat pays gold and xp as a consolation, and takes 💜 away: affinity is what a win is for, and a scale that only ever grows measures time played rather than play.',
  affinity:
    'Affinity: gains for dialogue answers, for the daily “attention” and for a first scene watch. Together they are the free daily income — keep it well below the step between scene gates, or gifts stop being worth buying.',
  chat: 'Assistant chat: how long a silence before she writes, and how close to the scene gate she starts hinting at it. Gains for answers live under affinity — that is affinity, and it is calibrated together with the rest of it.',
}

export function Economy(): JSX.Element {
  const [data, setData] = useState<ConfigsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<GameConfigKey>(GAME_CONFIG_KEYS[0] ?? 'energy')
  const [version, setVersion] = useState(0)

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await request<ConfigsResponse>('/admin/configs'))
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load, version])

  const byKey = useMemo(() => {
    const map = new Map<string, ConfigRow>()
    for (const row of data?.configs ?? []) map.set(row.key, row)
    return map
  }, [data])

  const keys = data?.keys ?? GAME_CONFIG_KEYS
  const current = byKey.get(selected)
  const detached = (data?.configs ?? []).filter((c) => c.source === 'admin')

  return (
    <>
      <PageHead
        title="Economy"
        subtitle="The game's numbers live in the game_configs table and are edited from here, with no deploy. Every key is checked by its own schema: a value that fails it never reaches the database."
      />

      <div className="grid grid--sidebar">
        <Card
          title="Keys"
          /**
           * Сводка по владению — первое, что видно в разделе. Отвязанный
           * ключ живёт по своим правилам: файл в репозитории его больше
           * не обновляет, и знать, сколько таких, надо не заглядывая
           * в каждый по очереди.
           */
          hint={
            detached.length === 0
              ? 'Every key is refreshed from the files on deploy.'
              : `Detached from the files: ${detached.length} of ${keys.length}.`
          }
          padded={false}
        >
          <nav className="sidebar__nav" style={{ padding: 'var(--gap-sm)' }}>
            {keys.map((key) => {
              const row = byKey.get(key)
              return (
                <button
                  key={key}
                  type="button"
                  className={`nav-item${key === selected ? ' nav-item--active' : ''}`}
                  onClick={() => setSelected(key)}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, width: '100%' }}>
                    <span className="mono" style={{ fontSize: 13 }}>
                      {key}
                    </span>
                    <span className="row" style={{ gap: 6 }}>
                      {row === undefined ? (
                        <span className="badge badge--warn">not in the database</span>
                      ) : (
                        <>
                          <SourceBadge source={row.source} />
                          <span style={{ fontSize: 11 }} className="muted">
                            v{row.version}
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                </button>
              )
            })}
          </nav>
        </Card>

        <div className="stack">
          {error !== null && <Notice tone="error">{error}</Notice>}

          <Card
            title={selected}
            hint={DESCRIPTIONS[selected] ?? 'A game config.'}
            actions={
              current === undefined ? (
                <span className="badge badge--warn">not in the database</span>
              ) : (
                <>
                  <span className="badge">version {current.version}</span>
                  <SourceBadge source={current.source} />
                  {current.source === 'admin' && (
                    <ReclaimButton
                      config={current}
                      filesError={data?.filesError ?? null}
                      onDone={() => setVersion((v) => v + 1)}
                    />
                  )}
                </>
              )
            }
          >
            {current === undefined ? (
              <Notice tone="warn">
                Config “{selected}” is not in the database — it was never seeded. Run `pnpm seed`
                locally; in production the seed runs on every deploy.
              </Notice>
            ) : (
              <>
                {current.source === 'seed' ? (
                  <div style={{ marginBottom: 'var(--gap)' }}>
                    <Notice tone="warn">
                      This row is owned by the seed: the next deploy will overwrite it
                      with the value from content/economy.json. The first save from here
                      hands the config over to the admin panel — you can give it back to
                      the file with the “Reclaim by file” button right here.
                    </Notice>
                  </div>
                ) : (
                  <div style={{ marginBottom: 'var(--gap)' }}>
                    <Notice>
                      This config is detached from the file: edited by {current.updatedByEmail ?? 'an administrator'},
                      deploys no longer touch it.{' '}
                      {current.fileValue === null
                        ? 'The value from the file is unavailable.'
                        : diffLeaves(current.value, current.fileValue).length === 0
                          ? 'The value still matches content/economy.json.'
                          : `The file suggests something else: ${diffLeaves(current.value, current.fileValue).length} differences.`}
                    </Notice>
                  </div>
                )}

                <EditForm
                  value={current.value}
                  labels={LABELS}
                  hints={HINTS}
                  validate={(draft) => validateConfig(selected, draft)}
                  onSubmit={async (draft) => {
                    await request(`/admin/configs/${selected}`, { method: 'PUT', body: { value: draft } })
                    setVersion((v) => v + 1)
                  }}
                  meta={
                    <span className="muted" style={{ fontSize: 12 }}>
                      edited by: {current.updatedByEmail ?? (current.source === 'seed' ? 'the seed' : '—')} ·{' '}
                      {formatDate(current.updatedAt)}
                    </span>
                  }
                />
              </>
            )}
          </Card>

          <Card title="Change log" hint="Every edit made here writes a row into admin_audit." padded={false}>
            <AuditTrail entity="game_configs" entityId={selected} refreshKey={version} />
          </Card>
        </div>
      </div>
    </>
  )
}

/**
 * «Вернуть к файлу» — снять владение админки и восстановить значение
 * из `content/economy.json`.
 *
 * Кнопка двухшаговая, и это не перестраховка. Действие стирает калибровку,
 * которую человек подбирал по живым данным, откатить его нечем (в базе
 * останется только строка аудита), а стоит оно одного клика рядом с
 * безобидным бейджем. Поэтому второй шаг показывает ровно то, что
 * изменится, — а не спрашивает «вы уверены?», на что все всегда уверены.
 */
function ReclaimButton({
  config,
  filesError,
  onDone,
}: {
  config: ConfigRow
  filesError: string | null
  onDone: () => void
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changes = config.fileValue === null ? [] : diffLeaves(config.value, config.fileValue)

  if (!open) {
    return (
      <button className="btn btn--sm" type="button" onClick={() => setOpen(true)}>
        Reclaim by file
      </button>
    )
  }

  return (
    <div
      className="stack stack--sm"
      style={{
        flexBasis: '100%',
        padding: 'var(--gap)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface-2)',
      }}
    >
      <strong style={{ fontSize: 13 }}>Hand “{config.key}” back to the seed?</strong>

      {filesError !== null ? (
        <Notice tone="error">
          The content files cannot be read, so there is nothing to reclaim: {filesError}
        </Notice>
      ) : config.fileValue === null ? (
        <Notice tone="warn">
          content/economy.json has no such key — there is nothing to reclaim.
        </Notice>
      ) : changes.length === 0 ? (
        <p className="card__hint" style={{ margin: 0 }}>
          The value will not change — it already matches the file. Only the owner
          changes: deploys will update this row again.
        </p>
      ) : (
        <>
          <p className="card__hint" style={{ margin: 0 }}>
            The current calibration will be replaced by the value from the file.
            The only way back is to edit it again.
          </p>
          <div className="audit__diff" style={{ marginTop: 0 }}>
            {changes.join('\n')}
          </div>
        </>
      )}

      {error !== null && <Notice tone="error">{error}</Notice>}

      <div className="row">
        <button
          className="btn btn--sm"
          type="button"
          disabled={busy || filesError !== null || config.fileValue === null}
          onClick={() => {
            setBusy(true)
            setError(null)
            request(`/admin/configs/${config.key}/reclaim`, { method: 'POST' })
              .then(() => {
                setOpen(false)
                onDone()
              })
              .catch((err: Error) => setError(err.message))
              .finally(() => setBusy(false))
          }}
        >
          {busy ? 'Reclaiming…' : 'Yes, hand it back'}
        </button>
        <button className="btn btn--ghost btn--sm" type="button" disabled={busy} onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </div>
  )
}

/**
 * Проверка ровно той схемой, которой проверит сервер. Импорт из
 * `@lust/shared` здесь принципиален: своя копия правил в админке разошлась
 * бы с серверной, и форма начала бы отклонять то, что сервер принимает
 * (или, хуже, наоборот).
 */
function validateConfig(key: GameConfigKey, draft: unknown): string | null {
  try {
    parseGameConfig(key, draft)
    return null
  } catch (err) {
    return err instanceof Error ? err.message : 'the value failed validation'
  }
}
